import { useState, type FormEvent } from "react";
import { Button, Input } from "../lib/ui.js";
import { BookingApiError, type BookingApi } from "./api.js";
import { errorMessage } from "./helpers.js";

/**
 * The business asks for a date of birth: the customer types the one on file, after their mobile has been checked. Three wrong tries end the
 * session (the server ends it; `onEnded` tells the page to ask for a new code).
 */
export function DobCheck({ api, token, onOk, onEnded }: { api: BookingApi; token: string; onOk: () => void; onEnded: (note: string) => void }) {
  const [dob, setDob] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function check(e: FormEvent) {
    e.preventDefault();
    if (!dob) return;
    setBusy(true);
    setError(null);
    try {
      await api.verifyDob(token, dob);
      onOk();
    } catch (err) {
      const code = err instanceof BookingApiError ? err.code : "unexpected_error";
      if (code === "dob_too_many" || code === "invalid_session") onEnded(errorMessage(code));
      else setError(errorMessage(code, null, err instanceof BookingApiError ? err.attemptsLeft : null));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={check} className="bk-stack" style={{ gap: 10 }}>
      <label className="bk-label">
        Date of birth
        <Input type="date" value={dob} min="1900-01-01" onChange={(e) => setDob(e.target.value)} autoComplete="bday" required />
      </label>
      <p className="bk-hint" style={{ margin: 0 }}>To protect your details, please confirm your date of birth.</p>
      <Button type="submit" variant="primary" disabled={busy || !dob}>{busy ? "Checking..." : "Check"}</Button>
      {error && <p className="bk-error" role="alert" style={{ margin: 0 }}>{error}</p>}
    </form>
  );
}
